import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

export type FieldType = 'account-type' | 'period-selector' | 'period-dropdown' | 'select' | 'hidden' | 'custom';

export interface FieldOption {
  value: string | number;
  label: string;
}

export interface CertificateField {
  type: FieldType;
  fieldName: string;
  label: string;
  required: boolean;
  options?: FieldOption[];
  defaultValue?: string | number;
  includeAllOption?: boolean;
  placeholder?: string;
}

export interface CertificateConfig {
  name: string;
  description: string;
  disclosure: string;
  path: string;
  category: string;
  index: number;
  tooltip?: string;
  usageTitle: string;
  usageItems: string[];
  fields?: CertificateField[];
  emailEndpoint?: string;
  printEndpoint?: string;
}

interface CertificateConfigResponse {
  success: boolean;
  data: CertificateConfig;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class CertificateConfigService {
  private readonly API_URL = environment.apiUrl;
  private readonly cache = new Map<number, CertificateConfig>();
  private readonly loading = signal<boolean>(false);
  private readonly error = signal<string | null>(null);

  readonly isLoading = this.loading.asReadonly();
  readonly errorSignal = this.error.asReadonly();

  /**
   * Obtiene la configuración de un certificado por su índice
   */
  async getConfig(index: number): Promise<CertificateConfig> {
    // Verificar caché
    if (this.cache.has(index)) {
      return this.cache.get(index)!;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      const response = await fetch(`${this.API_URL}/certificates/${index}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `Error ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || 'Error al obtener configuración del certificado');
      }

      const responseData: CertificateConfigResponse = await response.json();
      const config = responseData.data;

      // Guardar en caché
      this.cache.set(index, config);

      return config;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar configuración';
      this.error.set(errorMessage);
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Limpia la caché de configuraciones
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Precarga configuraciones de certificados
   */
  async preloadConfigs(indexes: number[]): Promise<void> {
    const promises = indexes.map(index => this.getConfig(index).catch(err => {
      console.error(`Error precargando configuración del certificado ${index}:`, err);
    }));
    await Promise.all(promises);
  }
}
